import React, { useCallback, useEffect, useState } from 'react';
import { Button, Popconfirm, Segmented, Select, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import PageHeader from '../../../shared/components/PageHeader';
import hostSharedAdminService from '../../services/hostSharedAdminService';
import { useTranslation } from '../../../shared/i18n';

const { Text } = Typography;
const STATUS = { pending: ['待开通', 'processing'], placed: ['已开通', 'success'], cancelled: ['已取消', 'default'] };

/** Admin: paid 共享主机 orders waiting for a node (资源不足). */
const Pending = () => {
    const { t } = useTranslation();
    const [status, setStatus] = useState('pending');
    const [data, setData] = useState({ items: [], nodes: [] });
    const [loading, setLoading] = useState(false);
    const [pick, setPick] = useState({}); // subscription id → node id
    const [acting, setActing] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const r = await hostSharedAdminService.getPending(status);
        setLoading(false);
        setData(r?.success ? r.data : { items: [], nodes: [] });
    }, [status]);
    useEffect(() => { load(); }, [load]);

    const act = async (key, p) => {
        setActing(key);
        const r = await p;
        setActing(null);
        if (r?.success) { message.success(t(r.message || '已提交')); load(); } else message.error(r?.error || t('Operation failed'));
    };

    const nodeName = (id) => data.nodes.find((n) => n.id === id)?.name || `#${id}`;

    const columns = [
        { title: t('Subscription'), dataIndex: 'subscription_id', width: 80, render: (v) => `#${v}` },
        { title: t('User'), dataIndex: 'user_id', width: 80, render: (v) => `#${v}` },
        {
            title: t('Product'), dataIndex: 'product', render: (v, r) => (
                <Space direction="vertical" size={0}>
                    <span>{v || `#${r.product_id}`}</span>
                    <Text type="secondary" style={{ fontSize: 12 }}>{[r.domain, r.region].filter(Boolean).join(' · ')}</Text>
                </Space>
            ),
        },
        { title: t('Status'), dataIndex: 'status', width: 110, render: (v, r) => <Tag color={STATUS[v]?.[1]}>{t(STATUS[v]?.[0] || v)}{r.vhost_id ? ` #${r.vhost_id}` : ''}</Tag> },
        {
            title: t('Reason'), key: 'tried', render: (_, r) => {
                const rs = r.tried || [];
                if (!rs.length) return r.last_error ? <Text type="secondary" style={{ fontSize: 12 }}>{r.last_error}</Text> : '-';
                return (
                    <Tooltip title={<div>{rs.map((x, i) => <div key={i}>{x.node || nodeName(x.node_id)}: {x.reason}</div>)}</div>}>
                        <Text type="secondary" style={{ fontSize: 12 }} ellipsis>{`${t('{{n}} tries', { n: r.attempts })} · ${rs[0].node || nodeName(rs[0].node_id)}: ${rs[0].reason}`}</Text>
                    </Tooltip>
                );
            },
        },
        { title: t('Created at'), dataIndex: 'created_at', width: 160, render: (v, r) => <Tooltip title={r.last_try_at ? `${t('Last attempt')} ${r.last_try_at}` : null}>{v}</Tooltip> },
        {
            title: t('Actions'), key: 'op', width: 340, render: (_, r) => (r.status !== 'pending' ? null : (
                <Space size={4} wrap>
                    <Button size="small" loading={acting === `r${r.subscription_id}`}
                        onClick={() => act(`r${r.subscription_id}`, hostSharedAdminService.retryPending(r.subscription_id))}>{t('Retry now')}</Button>
                    <Space.Compact size="small">
                        <Select size="small" allowClear showSearch optionFilterProp="label" placeholder={t('Pick node')} style={{ width: 150 }}
                            value={pick[r.subscription_id]} onChange={(v) => setPick((s) => ({ ...s, [r.subscription_id]: v }))}
                            options={data.nodes.map((n) => ({ value: n.id, label: `${n.name} (${n.used}/${n.capacity || '∞'})`, disabled: n.status !== 'online' }))} />
                        <Button size="small" disabled={!pick[r.subscription_id]} loading={acting === `n${r.subscription_id}`}
                            onClick={() => act(`n${r.subscription_id}`, hostSharedAdminService.retryPending(r.subscription_id, pick[r.subscription_id]))}>{t('Enable')}</Button>
                    </Space.Compact>
                    <Popconfirm title={t('Cancel and refund?')} okButtonProps={{ danger: true }}
                        onConfirm={() => act(`c${r.subscription_id}`, hostSharedAdminService.cancelPending(r.subscription_id))}>
                        <Button size="small" danger loading={acting === `c${r.subscription_id}`}>{t('Cancel & refund')}</Button>
                    </Popconfirm>
                </Space>
            )),
        },
    ];

    return (
        <div>
            <PageHeader title={t('Pending provisioning')} extra={<Button icon={<ReloadOutlined />} onClick={load} loading={loading} />} />
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Segmented value={status} onChange={setStatus}
                    options={['pending', 'placed', 'cancelled', 'all'].map((v) => ({ value: v, label: v === 'all' ? t('All') : t(STATUS[v][0]) }))} />
                <Table size="small" rowKey="subscription_id" loading={loading} dataSource={data.items} columns={columns}
                    pagination={{ pageSize: 50, hideOnSinglePage: true }} scroll={{ x: 1100 }} />
            </Space>
        </div>
    );
};

export default Pending;
